import React from 'react';
import { Form, useActionData, useNavigate, useNavigation, useSubmit } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions
} from '@oysterjs/ui/dialog';
import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Combobox } from '@oysterjs/ui/combobox';
import { ValidationError } from '@oysterjs/core/graphql';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import {
  AgencyUserFieldsFragment,
  AgencyUserInput,
  DisableAgencyUserDocument,
  DisableAgencyUserMutation,
  DisableAgencyUserMutationVariables,
  EnableAgencyUserDocument,
  EnableAgencyUserMutation,
  EnableAgencyUserMutationVariables,
  UpdateAgencyUserDocument,
  UpdateAgencyUserInput,
  UpdateAgencyUserMutation,
  UpdateAgencyUserMutationVariables,
  Role,
  useGetAgencyProfileOptionsQuery,
  useGetAgencyUserProfileOptionsQuery
} from '../../../api/graphql';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import {
  buildUserProfileOptions,
  decodeUserProfileOption,
  encodeUserProfileOption,
  teamAssignmentNotice,
  userProfileChoiceOption,
  userProfileFieldLabel
} from './user-profile-options';

// Generic type for mutations that return createAgencyUser
type CreateAgencyUserResult = {
  createAgencyUser?: AgencyUserFieldsFragment | null;
};

type AddUserDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Title for the dialog. Defaults to "Add User".
   */
  title?: string;
  /**
   * Description for the dialog.
   */
  description?: string;
  /**
   * The agency whose profiles the field offers. Omit for the caller's own
   * agency; child-agency pages pass the agency they are editing.
   */
  agencyId?: string;
};

/**
 * Shared dialog for adding a user. Works for both parent and child agencies.
 * The calling route must have an action configured that handles the user creation mutation.
 */
export const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
  title = 'Add User',
  description = "Add a new user. Once you add them, they'll be able to sign in and use Cara.",
  agencyId
}) => {
  const submit = useSubmit();
  const navigation = useNavigation();
  const action = useActionData() as ActionResult<CreateAgencyUserResult> | undefined;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<AgencyUserInput>({
    name: '',
    email: '',
    roles: [Role.User],
    profileId: null
  });

  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  // Fetched only while the dialog is open: an agency's profiles are settings
  // data, so reading them on every page that can add a user would be wasted.
  const { data: options } = useGetAgencyProfileOptionsQuery({
    variables: { agencyId },
    skip: !open
  });
  const profiles = options?.agencyProfiles ?? [];
  const profileOptions = buildUserProfileOptions(profiles);
  const selectedProfileOption = userProfileChoiceOption({
    roles: form.roles ?? [],
    profileId: form.profileId ?? null
  });

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (action?.applicationError) {
      setError(action.applicationError.message);
    }
  }, [action?.applicationError]);

  React.useEffect(() => {
    if (action?.data?.createAgencyUser) {
      setError(null);
      setValidationErrors([]);
      setForm({ name: '', email: '', roles: [Role.User], profileId: null });
      onClose();
      onSuccess?.();
    }
  }, [action?.data?.createAgencyUser, onClose, onSuccess]);

  const handleClose = () => {
    setError(null);
    setValidationErrors([]);
    setForm({ name: '', email: '', roles: [Role.User], profileId: null });
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <Form method="POST" onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  placeholder="John Smith"
                  type="text"
                  value={form.name}
                  onChange={(e) => {
                    const name = e.currentTarget.value;
                    updateForm(['name'], (prev) => ({ ...prev, name }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['name'])}
                />
                {!!getValidationError(validationErrors, ['name']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['name'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Email</Label>
                <Input
                  placeholder="john.smith@example.com"
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    const email = e.currentTarget.value;
                    updateForm(['email'], (prev) => ({ ...prev, email }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['email'])}
                />
                {!!getValidationError(validationErrors, ['email']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['email'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>{userProfileFieldLabel(profiles)}</Label>
                <Combobox
                  value={selectedProfileOption}
                  minQueryLength={0}
                  maxOptions={1000}
                  onChange={(value: string) => {
                    const choice = decodeUserProfileOption(value);
                    // One control writes both fields, so it clears the errors on both.
                    updateFormMulti([['roles'], ['profileId']], (prev) => ({
                      ...prev,
                      roles: choice.roles,
                      profileId: choice.profileId
                    }));
                  }}
                  options={profileOptions}
                />
                {!!getValidationError(validationErrors, ['profileId']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['profileId'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
          {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}
        </DialogBody>
        <DialogActions>
          <Button color="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="sky" type="submit" loading={navigation.state === 'submitting'}>
            Add User
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

type DisableUserDialogProps = {
  client: ApolloClient<NormalizedCacheObject>;
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Optional user name to display in the confirmation message.
   */
  userName?: string;
  /**
   * Path to navigate to after closing. If not provided, no navigation occurs.
   */
  navigateTo?: string;
  /**
   * If true, skip navigation even if navigateTo is provided.
   * Useful when the dialog is used in a context where navigation is handled externally.
   */
  skipNavigation?: boolean;
};

/**
 * Shared dialog for disabling a user. Works for both parent and child agency users.
 */
export const DisableUserDialog: React.FC<DisableUserDialogProps> = ({
  client,
  open,
  userId,
  onClose,
  onSuccess,
  userName,
  navigateTo,
  skipNavigation
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDisable = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await client.mutate<
        DisableAgencyUserMutation,
        DisableAgencyUserMutationVariables
      >({
        mutation: DisableAgencyUserDocument,
        variables: { id: userId }
      });

      if (res.errors?.length) {
        setError(res.errors[0].message);
      } else {
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!open) return;
    setError(null);
    onClose();
    if (navigateTo && !skipNavigation) {
      navigate(navigateTo);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Disable User</DialogTitle>
      <DialogDescription>
        Are you sure you want to disable {userName ? <strong>{userName}</strong> : 'this user'}?
        They will no longer be able to access the system. This will free up a seat in the
        subscription.
      </DialogDescription>
      <DialogBody>{error && <ErrorMessage>{error}</ErrorMessage>}</DialogBody>
      <DialogActions>
        <Button color="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="red" onClick={handleDisable} loading={loading}>
          Disable User
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type EnableUserDialogProps = {
  client: ApolloClient<NormalizedCacheObject>;
  open: boolean;
  userId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * Optional user name to display in the confirmation message.
   */
  userName?: string;
  /**
   * Path to navigate to after closing. If not provided, no navigation occurs.
   */
  navigateTo?: string;
};

/**
 * Shared dialog for enabling a user. Works for both parent and child agency users.
 */
export const EnableUserDialog: React.FC<EnableUserDialogProps> = ({
  client,
  open,
  userId,
  onClose,
  onSuccess,
  userName,
  navigateTo
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleEnable = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await client.mutate<EnableAgencyUserMutation, EnableAgencyUserMutationVariables>({
        mutation: EnableAgencyUserDocument,
        variables: { id: userId }
      });

      if (res.errors?.length) {
        setError(res.errors[0].message);
      } else {
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!open) return;
    setError(null);
    onClose();
    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Enable User</DialogTitle>
      <DialogDescription>
        Are you sure you want to enable {userName ? <strong>{userName}</strong> : 'this user'}? They
        will be able to access the system again. This will consume a seat from the subscription.
      </DialogDescription>
      <DialogBody>{error && <ErrorMessage>{error}</ErrorMessage>}</DialogBody>
      <DialogActions>
        <Button color="light" onClick={handleClose}>
          Cancel
        </Button>
        <Button color="sky" onClick={handleEnable} loading={loading}>
          Enable User
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type EditUserDialogProps = {
  client: ApolloClient<NormalizedCacheObject>;
  open: boolean;
  user: AgencyUserFieldsFragment | null;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * The agency whose profiles the field offers. Omit for the caller's own
   * agency; child-agency pages pass the agency they are editing.
   */
  agencyId?: string;
  /**
   * Path to navigate to after closing. If not provided, no navigation occurs.
   */
  navigateTo?: string;
  /**
   * If true, skip navigation even if navigateTo is provided.
   */
  skipNavigation?: boolean;
};

/**
 * Shared dialog for editing a user's profile. Works for both parent and child agency users.
 */
export const EditUserDialog: React.FC<EditUserDialogProps> = ({
  client,
  open,
  user,
  onClose,
  onSuccess,
  navigateTo,
  skipNavigation,
  agencyId
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<UpdateAgencyUserInput>({
    roles: []
  });

  const { data: options } = useGetAgencyUserProfileOptionsQuery({
    variables: { agencyId, agencyUserId: user?.id ?? '' },
    skip: !open || !user
  });
  const profiles = options?.agencyProfiles ?? [];
  const assignment = options?.agencyUserProfileAssignment;
  const profileOptions = buildUserProfileOptions(profiles);

  // The stored assignment seeds the field once it arrives, and any edit the
  // admin has made since wins: form.profileId is undefined until they touch it.
  const selectedProfileOption =
    form.profileId === undefined
      ? encodeUserProfileOption(form.roles ?? [], assignment)
      : userProfileChoiceOption({ roles: form.roles ?? [], profileId: form.profileId ?? null });
  const notice = teamAssignmentNotice(selectedProfileOption, assignment);

  React.useEffect(() => {
    if (user) {
      setForm({ roles: user.roles });
    }
  }, [user]);

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const res = await client.mutate<UpdateAgencyUserMutation, UpdateAgencyUserMutationVariables>({
        mutation: UpdateAgencyUserDocument,
        variables: { id: user.id, input: form }
      });

      if (res.errors?.length) {
        setError(res.errors[0].message);
      } else {
        onSuccess?.();
        handleClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!open) return;
    setError(null);
    onClose();
    if (navigateTo && !skipNavigation) {
      navigate(navigateTo);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={handleUpdate}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogDescription>Update user role and permissions.</DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <div data-slot="control" className="text-sm text-neutral-900 dark:text-white">
                  {user?.name || '—'}
                </div>
              </Field>
              <Field>
                <Label>Email</Label>
                <div data-slot="control" className="text-sm text-neutral-900 dark:text-white">
                  {user?.email || '—'}
                </div>
              </Field>
              <Field>
                <Label>{userProfileFieldLabel(profiles)}</Label>
                <Combobox
                  value={selectedProfileOption}
                  minQueryLength={0}
                  maxOptions={1000}
                  onChange={(value: string) => {
                    const choice = decodeUserProfileOption(value);
                    setForm((prev) => ({
                      ...prev,
                      roles: choice.roles,
                      profileId: choice.profileId
                    }));
                  }}
                  options={profileOptions}
                />
                {!!notice && <Description>{notice}</Description>}
              </Field>
            </FieldGroup>
          </Fieldset>
          {error && <ErrorMessage className="mt-4">{error}</ErrorMessage>}
        </DialogBody>
        <DialogActions>
          <Button color="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="sky" type="submit" loading={loading}>
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
